import React, { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Menu,
  MenuItem,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@mui/material';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EqualizerOutlinedIcon from '@mui/icons-material/EqualizerOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { MoneyOutlined } from '@mui/icons-material';
import { AggregateLegCharges } from 'utils/calculations';
import { formatCurrency } from 'utils/formatter';
import DataTable from 'components/DataTable';

const LOCALE = 'USD';
const BillTable = (props) => {
  const {
    bills,
    page,
    pageSize,
    rowCount,    
    fnViewLegHandler,
    fnViewOrderHandler,
    fnLegChargeHandler,
    fnDriverRateHandler,
    fnViewRemittanceHandler,
    fnFileUploadHandler,
    fnOnRowSelection,
    fnOnPageChange,
    fnOnPageSizeChange,
  } = props;
  //const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null); //Single bill. usually through menu

  useEffect(() => {
    // // Notify parent about the selected rows
    fnOnRowSelection(selectedRows);
  }, [selectedRows]);

  // Handle selection change in DataGrid and notify parent
  const handleSelectedRowChange = (rows) => {
    const bills_with_custom_amount = bills.map(bill => {      
      const legCharges = AggregateLegCharges(bill.legCharges);
      const billAmount = bill.billAmount;
      const totalAmount = billAmount + legCharges;      
      return rows.includes(bill.id)
        ? { ...bill, customAmount: (totalAmount - bill.amountPaid) } // Add new property to the object
        : bill
      }
    );
    
    const filteredRows = bills_with_custom_amount.filter(bill => rows.includes(bill.id));
    setSelectedRows(filteredRows);
  };

  const toggleActionMenu = (evt, bill) => {
    setSelectedBill(bill);
    const target = evt ? evt.currentTarget : null;
    setAnchorEl(target);
  };

  const renderCustomAmount = (rowId) => {
    const filtered = selectedRows.filter(bill => bill.id === rowId);
    if(filtered && filtered.length > 0) {
      return filtered[0].customAmount;
    }
    return 0;
  };

  const handleAmountReceivedChange = (evt, rowId) => {
    const userAmount = Number(evt.target.value);
    const updated = selectedRows.map(bill => bill.id === rowId ? {...bill, customAmount: userAmount } : { ...bill });
    setSelectedRows(updated);
  };

  const tableHeader = [
    {field: 'legId', label: 'Leg Id', numeric: false, disablePadding: true,  visible: true},
    {field: 'orderId',  label: 'Load Id', numeric: false, disablePadding: true,  visible: true},
    {field: 'orderPaymentStatus', label: 'Load Payment Status', numeric: false, disablePadding: true,  visible: true},
    {field: 'billAmount', label: 'Driver Pay',numeric: false, disablePadding: true,  visible: true},
    {field: 'legCharges', label: 'Charges', numeric: false, disablePadding: true,  visible: true},
    {field: 'amountPaid', label: 'Paid', numeric: false, disablePadding: true,  visible: true},
    {field: 'paymentReceived',label: 'Received', numeric: false, disablePadding: true,  visible: true},
    { field: 'billDate', label: 'Bill Date', numeric: false, disablePadding: true,  visible: false},
    { field: 'paymentDate', label: 'Payment Date',numeric: false, disablePadding: true,  visible: true },
    { field: 'action', label: 'Action',numeric: false, disablePadding: true,  visible: true },
  ];
  const createCustomBillDate = (bill) => {
    const {
      id,
      legId,
      orderId,
      displayOrderId,
      orderPaymentStatus,
      billDate,
      paymentDate,
      status,
    } = bill;
    const billAmount = formatCurrency(bill.billAmount, LOCALE, 'standard');
    const legCharges = formatCurrency(AggregateLegCharges(bill.legCharges), LOCALE, 'standard');
    const amountPaid = formatCurrency(bill.amountPaid, LOCALE, 'standard');
    const paymentReceived = formatCurrency(bill.paymentReceived, 'en-US');
    const row = {
      raw: {
        id,
        legId,
        orderId: displayOrderId,
        orderPaymentStatus,
        billAmount,
        legCharges,
        amountPaid,
        paymentReceived,
        billDate,
        paymentDate,
        status,
      },
      render: {
        legId: <Link color='primary' onClick={() => fnViewLegHandler(legId)} ><Typography variant='body2'>{legId}</Typography></Link>,
        orderId: <Link color='primary' onClick={() => fnViewOrderHandler(orderId)} ><Typography variant='body2'>{displayOrderId}</Typography></Link>,
        orderPaymentStatus: <Typography variant='body2'>{orderPaymentStatus}</Typography>,
        billAmount: <Typography variant='body2'>{billAmount}</Typography>,
        legCharges: <Typography variant='body2'>{legCharges}</Typography>,        
        amountPaid: <Typography variant='body2'>{amountPaid}</Typography>,
        paymentReceived: status === 'PAID' ? <Typography variant='body2'>{paymentReceived}</Typography>
            :<TextField
                size='small'
                margin='dense'
                variant='outlined'
                type='number'
                value={renderCustomAmount(id)}
                disabled={selectedRows.every(row => row.id !== id)}
                onChange={(evt) => handleAmountReceivedChange(evt, id)}
                sx={{maxWidth: 85}}
            />,
        billDate: <Typography variant='body2'>{billDate}</Typography>,
        paymentDate: <Typography variant='body2'>{paymentDate}</Typography>,
        action: <>
                  <IconButton
                    aria-label='settings'
                    onClick={(evt) => toggleActionMenu(evt, bill)}
                    size='large'>
                    <MoreVertIcon />
                  </IconButton>
                </>
      }
    };    
    return row;
  };  
  const tableData = bills.map(b => createCustomBillDate(b) );  
  return (
    <>
      <DataTable
        header={tableHeader.filter(cell=>cell.visible)}
        rows={tableData}
        sortKey={'billDate'}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}        
        handlePageChange={fnOnPageChange}
        handlePageSizeChange={fnOnPageSizeChange}
        handleCheckboxToggle={ selectedRows => handleSelectedRowChange(selectedRows)}
      />
      <Menu
        id='long-menu'
        anchorEl={anchorEl}
        open={open}
        keepMounted
        onClose={() => toggleActionMenu(null, null)}
      >
        <MenuItem
          key={`order:${selectedBill?.id}`}
          onClick={() => fnViewOrderHandler(selectedBill?.orderId)}                
        >
          <VisibilityOutlinedIcon fontSize='small' />
          &nbsp;&nbsp;View Order
        </MenuItem>
        {/* <Divider />
        <MenuItem
          key={`leg:${selectedBill?.id}`}
          onClick={() => fnViewLegHandler(selectedBill?.legId)}                
        >
          <VisibilityOutlinedIcon fontSize='small' />
          &nbsp;&nbsp;View Leg
        </MenuItem> */}
        <Divider />
        {/* <MenuItem
          key={`rate:${selectedBill?.id}`}
          onClick={() => fnDriverRateHandler(selectedBill)}                
        >
          <MoneyOutlined fontSize='small' />
          &nbsp;&nbsp;Change Driver Rate
        </MenuItem>               */}
        <Divider />                            
        <MenuItem
          key={`charge:${selectedBill?.id}`}
          onClick={() => fnLegChargeHandler(selectedBill)}
        >
        <MoneyOutlined fontSize='small' />
          &nbsp;&nbsp;Add Charges
        </MenuItem>
        <Divider />
        <MenuItem
          key={`stats:${selectedBill?.id}`}
          onClick={() => fnViewRemittanceHandler(selectedBill)}
        >
          <EqualizerOutlinedIcon fontSize='small' />
          &nbsp;&nbsp;View Remittance
        </MenuItem>
        <Divider />
        <MenuItem
          key={`files:${selectedBill?.id}`}
          onClick={() => fnFileUploadHandler(selectedBill)}
        >
          <AttachFileIcon fontSize='small' />
          &nbsp;&nbsp;Upload Documents
        </MenuItem>
      </Menu>      
    </>
  );
};

export default BillTable;